/**
 * Dynamic Batching System with Memory Management
 * 
 * Intelligent batching system that adapts to memory pressure and
 * provides efficient data collection with overflow handling.
 */

import { CircularBuffer, CircularBufferConfig } from './circular-buffer'
import { MemorySizeEstimator } from './size-estimator'
import { Logger } from '@/utils/logger'

const logger = new Logger('DynamicBatcher')

export interface DynamicBatchConfig {
  minSize: number
  maxSize: number
  flushInterval: number
  memoryThresholdMB: number
  overflowStrategy: 'flush' | 'drop' | 'expand'
  enableMemoryMonitoring: boolean
  adaptiveSizing: boolean
}

export interface BatchMetrics {
  totalBatches: number
  totalItems: number
  averageBatchSize: number
  memoryUsageMB: number
  flushes: {
    size: number
    memory: number
    timeout: number
    manual: number
  }
  adaptations: {
    expansions: number
    contractions: number
    memoryPressure: number
  }
  performance: {
    averageFlushTime: number
    averageMemoryEstimationTime: number
  }
}

export class DynamicBatcher<T> {
  private buffer: CircularBuffer<T>
  private config: DynamicBatchConfig
  private flushTimer?: NodeJS.Timeout
  private isShutdown = false

  // Metrics
  private metrics: BatchMetrics = {
    totalBatches: 0,
    totalItems: 0,
    averageBatchSize: 0,
    memoryUsageMB: 0,
    flushes: { size: 0, memory: 0, timeout: 0, manual: 0 },
    adaptations: { expansions: 0, contractions: 0, memoryPressure: 0 },
    performance: { averageFlushTime: 0, averageMemoryEstimationTime: 0 }
  }

  // Adaptive sizing state
  private currentMaxSize: number
  private lastMemoryCheck = 0
  private memoryCheckInterval = 1000 // Check memory every second
  private flushTimes: number[] = []
  private estimationTimes: number[] = []

  // Flush handlers
  private flushHandlers: Array<(items: T[]) => Promise<void> | void> = []

  constructor(config: DynamicBatchConfig) {
    this.config = { ...config }
    this.currentMaxSize = config.maxSize

    // Create circular buffer
    const bufferConfig: CircularBufferConfig = {
      maxSize: config.maxSize * 2, // Allow some expansion
      overflowStrategy: config.overflowStrategy === 'expand' ? 'expand' : 'overwrite',
      shrinkThreshold: 0.25
    }
    
    this.buffer = new CircularBuffer<T>(bufferConfig)
    
    // Start flush timer
    this.startFlushTimer()
    
    logger.info('Dynamic batcher initialized', {
      minSize: config.minSize,
      maxSize: config.maxSize,
      memoryThreshold: config.memoryThresholdMB,
      strategy: config.overflowStrategy
    })
  }

  /**
   * Add item to batch
   */
  async add(item: T): Promise<boolean> {
    if (this.isShutdown) {
      return false
    }

    const added = this.buffer.write(item)
    
    if (added) {
      this.metrics.totalItems++
      
      // Check if we should flush due to size
      if (this.buffer.size() >= this.currentMaxSize) {
        await this.flush('size')
        return true
      }

      // Check memory pressure if enabled
      if (this.config.enableMemoryMonitoring && this.shouldCheckMemory()) {
        const memoryPressure = await this.checkMemoryPressure()
        if (memoryPressure.shouldFlush) {
          await this.flush('memory')
        }
      }
    }

    return added
  }

  /**
   * Add multiple items at once
   */
  async addBatch(items: T[]): Promise<number> {
    let added = 0
    
    for (const item of items) {
      if (await this.add(item)) {
        added++
      } else {
        break // Stop if we can't add more
      }
    }
    
    return added
  }

  /**
   * Manually trigger flush
   */
  async flush(reason: 'size' | 'memory' | 'timeout' | 'manual' = 'manual'): Promise<number> {
    if (this.buffer.isEmpty()) {
      return 0
    }

    const startTime = Date.now()
    const items = this.buffer.readBatch(this.buffer.size())
    
    if (items.length === 0) {
      return 0
    }

    // Update metrics
    this.metrics.flushes[reason]++
    this.metrics.totalBatches++
    this.updateAverageBatchSize(items.length)

    // Execute flush handlers
    try {
      await Promise.all(this.flushHandlers.map(handler => handler(items)))
      
      const flushTime = Date.now() - startTime
      this.updateAverageFlushTime(flushTime)
      
      logger.debug('Batch flushed', {
        reason,
        itemCount: items.length,
        flushTime,
        bufferSize: this.buffer.size()
      })
      
      return items.length
    } catch (error) {
      logger.error('Flush error', error)
      
      // Put items back on error (if buffer has space)
      for (let i = items.length - 1; i >= 0; i--) {
        if (!this.buffer.write(items[i])) {
          break // Buffer full, lose some items
        }
      }
      
      throw error
    }
  }

  /**
   * Add flush handler
   */
  onFlush(handler: (items: T[]) => Promise<void> | void): void {
    this.flushHandlers.push(handler)
  }

  /**
   * Remove flush handler
   */
  removeFlushHandler(handler: (items: T[]) => Promise<void> | void): void {
    const index = this.flushHandlers.indexOf(handler)
    if (index !== -1) {
      this.flushHandlers.splice(index, 1)
    }
  }

  /**
   * Get current metrics
   */
  getMetrics(): BatchMetrics {
    const bufferMetrics = this.buffer.getMetrics()
    
    return {
      ...this.metrics,
      memoryUsageMB: bufferMetrics.memoryUsageBytes / (1024 * 1024)
    }
  }

  /**
   * Get current status
   */
  getStatus(): {
    bufferSize: number
    bufferCapacity: number
    currentMaxSize: number
    isUnderMemoryPressure: boolean
    nextFlushIn: number
  } {
    const bufferMetrics = this.buffer.getMetrics()
    const memoryStatus = MemorySizeEstimator.checkMemoryPressure(this.config.memoryThresholdMB)
    
    return {
      bufferSize: bufferMetrics.size,
      bufferCapacity: bufferMetrics.capacity,
      currentMaxSize: this.currentMaxSize,
      isUnderMemoryPressure: memoryStatus.isUnderPressure,
      nextFlushIn: this.flushTimer ? this.config.flushInterval : 0
    }
  }

  /**
   * Shutdown batcher and flush remaining items
   */
  async shutdown(): Promise<void> {
    if (this.isShutdown) {
      return
    }

    this.isShutdown = true
    
    // Clear timer
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = undefined
    }

    // Flush remaining items
    try {
      await this.flush('manual')
      logger.info('Dynamic batcher shutdown complete')
    } catch (error) {
      logger.error('Error during shutdown flush', error)
    }
  }

  /**
   * Check if we should check memory usage
   */
  private shouldCheckMemory(): boolean {
    const now = Date.now()
    if (now - this.lastMemoryCheck >= this.memoryCheckInterval) {
      this.lastMemoryCheck = now
      return true
    }
    return false
  }

  /**
   * Check memory pressure and adapt if needed
   */
  private async checkMemoryPressure(): Promise<{ shouldFlush: boolean; adapted: boolean }> {
    const startTime = Date.now()
    
    try {
      const memoryStatus = MemorySizeEstimator.checkMemoryPressure(this.config.memoryThresholdMB)
      const estimationTime = Date.now() - startTime
      this.updateAverageEstimationTime(estimationTime)

      let shouldFlush = false
      let adapted = false

      if (memoryStatus.isUnderPressure) {
        shouldFlush = true
        
        // Adaptive sizing: reduce batch size under pressure
        if (this.config.adaptiveSizing && this.currentMaxSize > this.config.minSize) {
          const newSize = Math.max(
            this.config.minSize,
            Math.floor(this.currentMaxSize * 0.75)
          )
          
          if (newSize < this.currentMaxSize) {
            this.currentMaxSize = newSize
            this.metrics.adaptations.contractions++
            adapted = true
            
            logger.info('Reduced batch size due to memory pressure', {
              oldSize: this.currentMaxSize,
              newSize,
              memoryUsageMB: memoryStatus.heapUsedMB
            })
          }
        }
        
        this.metrics.adaptations.memoryPressure++
      } else if (this.config.adaptiveSizing && memoryStatus.pressureRatio < 0.5) {
        // Memory usage is low, we can expand batch size
        if (this.currentMaxSize < this.config.maxSize) {
          const newSize = Math.min(
            this.config.maxSize,
            Math.floor(this.currentMaxSize * 1.25)
          )
          
          if (newSize > this.currentMaxSize) {
            this.currentMaxSize = newSize
            this.metrics.adaptations.expansions++
            adapted = true
            
            logger.debug('Increased batch size due to low memory usage', {
              oldSize: this.currentMaxSize,
              newSize,
              memoryUsageMB: memoryStatus.heapUsedMB
            })
          }
        }
      }

      return { shouldFlush, adapted }
    } catch (error) {
      logger.error('Memory pressure check failed', error)
      return { shouldFlush: false, adapted: false }
    }
  }

  /**
   * Start the flush timer
   */
  private startFlushTimer(): void {
    if (this.isShutdown) {
      return
    }

    this.flushTimer = setTimeout(async () => {
      try {
        if (this.buffer.size() >= this.config.minSize) {
          await this.flush('timeout')
        }
      } catch (error) {
        logger.error('Timer flush error', error)
      } finally {
        this.startFlushTimer() // Restart timer
      }
    }, this.config.flushInterval)
  }

  /**
   * Update average batch size
   */
  private updateAverageBatchSize(batchSize: number): void {
    const total = this.metrics.averageBatchSize * (this.metrics.totalBatches - 1) + batchSize
    this.metrics.averageBatchSize = total / this.metrics.totalBatches
  }

  /**
   * Update average flush time
   */
  private updateAverageFlushTime(flushTime: number): void {
    this.flushTimes.push(flushTime)
    
    // Keep only last 100 measurements
    if (this.flushTimes.length > 100) {
      this.flushTimes.shift()
    }
    
    this.metrics.performance.averageFlushTime = 
      this.flushTimes.reduce((sum, time) => sum + time, 0) / this.flushTimes.length
  }

  /**
   * Update average estimation time
   */
  private updateAverageEstimationTime(estimationTime: number): void {
    this.estimationTimes.push(estimationTime)
    
    // Keep only last 100 measurements
    if (this.estimationTimes.length > 100) {
      this.estimationTimes.shift()
    }
    
    this.metrics.performance.averageMemoryEstimationTime = 
      this.estimationTimes.reduce((sum, time) => sum + time, 0) / this.estimationTimes.length
  }
}